import { Box, List, ListItem, Typography } from '@material-ui/core';
import DescriptionIcon from '@material-ui/icons/Description';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { Link } from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { parseEntityRef } from '@backstage/catalog-model';
import type { AgentToolCallTrace } from '../../api/MgaApi';
import { getAgentCitationGroups } from './agentSources';

function parseEntityRefSafe(entityRef: string) {
  try {
    return parseEntityRef(entityRef);
  } catch {
    return undefined;
  }
}

export const AgentSourcesView = ({
  toolCalls,
}: {
  toolCalls: AgentToolCallTrace[];
}) => {
  const config = useApi(configApiRef);
  const { entityRefs, docs, failedToolCalls } =
    getAgentCitationGroups(toolCalls);
  const appBaseUrl = config.getString('app.baseUrl');
  const techDocsPath = config.getOptionalString('ai.techDocsPath') ?? '/docs';
  const techDocsBaseUrl = `${appBaseUrl}${techDocsPath}`;

  if (
    entityRefs.length === 0 &&
    docs.length === 0 &&
    failedToolCalls.length === 0
  ) {
    return null;
  }

  return (
    <Box>
      <Typography variant="subtitle2" style={{ marginBottom: '0.5rem' }}>
        <strong>Sources and tools used:</strong>
      </Typography>

      {entityRefs.length > 0 && (
        <>
          <Typography
            variant="body2"
            style={{ marginBottom: '0.25rem', fontWeight: 500 }}
          >
            Catalog Entities ({entityRefs.length}):
          </Typography>
          <List dense style={{ paddingTop: 0 }}>
            {entityRefs.map(entityRef => (
              <ListItem key={entityRef} style={{ paddingLeft: '1rem' }}>
                <EntityRefLink entityRef={entityRef} defaultKind="Component" />
              </ListItem>
            ))}
          </List>
        </>
      )}

      {docs.length > 0 && (
        <>
          <Typography
            variant="body2"
            style={{ marginBottom: '0.25rem', fontWeight: 500 }}
          >
            Technical Documentation ({docs.length}):
          </Typography>
          <List dense style={{ paddingTop: 0 }}>
            {docs.map(doc => {
              const entity = parseEntityRefSafe(doc.entityRef);
              if (!entity) {
                return null;
              }
              const url = `${techDocsBaseUrl}/${entity.namespace}/${
                entity.kind
              }/${entity.name}${
                doc.path ? `/${doc.path.replace(/^\/+/, '')}` : ''
              }`;

              return (
                <ListItem
                  key={`${doc.entityRef}-${doc.path || doc.title}`}
                  style={{ paddingLeft: '1rem' }}
                >
                  <Link to={url}>
                    <Box
                      component="span"
                      display="inline-flex"
                      alignItems="center"
                    >
                      <DescriptionIcon fontSize="inherit" />
                      <Box component="span" marginLeft={0.5}>
                        {doc.title}
                      </Box>
                    </Box>
                  </Link>
                </ListItem>
              );
            })}
          </List>
        </>
      )}

      {failedToolCalls.length > 0 && (
        <>
          <Typography
            variant="body2"
            style={{ marginBottom: '0.25rem', fontWeight: 500 }}
          >
            Tool failures ({failedToolCalls.length}):
          </Typography>
          <List dense style={{ paddingTop: 0 }}>
            {failedToolCalls.map(toolCall => (
              <ListItem
                key={`${toolCall.step}-${toolCall.toolName}`}
                style={{ paddingLeft: '1rem' }}
              >
                <Box component="span" display="inline-flex" alignItems="center">
                  <ErrorOutlineIcon fontSize="inherit" />
                  <Box component="span" marginLeft={0.5}>
                    {toolCall.toolName}
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Box>
  );
};
